import React, { useEffect } from "react";
import Modal from "@/components/ui/Modal";
import FormGroup from "@/components/ui/FormGroup";
import Textinput from "@/components/ui/Textinput";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { protectedApi } from "@/store/api/api";
import { toast } from "react-toastify";

const FormValidationSchema = yup.object({
  businessName: yup.string().nullable(),
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  phoneNumber: yup.string().required("Phone number is required"),
});

const EditOrganiserModal = ({ isOpen, onClose, organiser, onSuccess }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(FormValidationSchema),
    defaultValues: {
      businessName: "",
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
    },
  });

  useEffect(() => {
    if (organiser && isOpen) {
      reset({
        businessName: organiser.businessName || organiser.nameOrganisingCompany || "",
        firstName: organiser.firstName || organiser.contactPerson?.firstName || "",
        lastName: organiser.lastName || organiser.contactPerson?.lastName || "",
        email: organiser.email || "",
        phoneNumber: organiser.phoneNumber || organiser.phone || "",
      });
    }
  }, [organiser, isOpen, reset]);

  const onSubmit = async (data) => {
    try {
      const response = await protectedApi.put(`/api/organisers/${organiser.id}`, {
        businessName: data.businessName || null,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phoneNumber: data.phoneNumber,
      });

      toast.success(response.data.message || "Organiser updated successfully");
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Failed to update organiser";
      toast.error(errorMessage);
    }
  };

  return (
    <Modal
      title="Edit Organiser Details"
      activeModal={isOpen}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <FormGroup error={errors.businessName}>
          <Textinput
            label="Business Name"
            type="text"
            placeholder="Enter business name"
            name="businessName"
            register={register}
            error={errors.businessName}
          />
        </FormGroup>

        <FormGroup error={errors.firstName}>
          <Textinput
            label="First Name"
            type="text"
            placeholder="Enter first name"
            name="firstName"
            register={register}
            error={errors.firstName}
          />
        </FormGroup>

        <FormGroup error={errors.lastName}>
          <Textinput
            label="Last Name"
            type="text"
            placeholder="Enter last name"
            name="lastName"
            register={register}
            error={errors.lastName}
          />
        </FormGroup>

        <FormGroup error={errors.email}>
          <Textinput
            label="Email"
            type="email"
            placeholder="Enter email"
            name="email"
            register={register}
            error={errors.email}
          />
        </FormGroup>

        <FormGroup error={errors.phoneNumber}>
          <Textinput
            label="Phone Number"
            type="text"
            placeholder="Enter phone number"
            name="phoneNumber"
            register={register}
            error={errors.phoneNumber}
          />
        </FormGroup>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-outline-dark"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Updating..." : "Update Organiser"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EditOrganiserModal;

